// Must precede './app' — the router is wrapped only if tracing registered first.
import './telemetry/instrument';
import { StrictMode } from 'react';
import { reactErrorHandler } from '@sentry/react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import { handleChunkError, clearReloadFlag } from '@/utils/chunk-error-handler';
import { App } from './app';
import { errorTracker } from './common/error-tracking';
import { getAppEnvironment, getAppVersion } from './config/env-config';
import { initiateSamlOrFallback } from './services/apis/saml-init.api';
import { setupAxiosInterceptors } from './services/axios-interceptors';
import { initializeTelemetry } from './telemetry/tracing';
import { BASE_PATH } from './utils/path-utils';

// Initialize OpenTelemetry trace context propagation
// This must be called before any HTTP requests are made
initializeTelemetry({
  serviceName: 'web-login',
  serviceVersion: getAppVersion(),
  environment: getAppEnvironment(),
});

// Setup global axios interceptors for error handling
setupAxiosInterceptors();

// Handle chunk loading errors (happens after deployments)
window.addEventListener('unhandledrejection', event => {
  handleChunkError(event.reason);
});

// Clear reload flag on successful load
window.addEventListener('load', () => {
  clearReloadFlag();
});

const reportReactErrorToSentry = reactErrorHandler();

function handleUncaughtReactError(
  error: unknown,
  errorInfo: { componentStack?: string | null }
) {
  const normalizedError =
    error instanceof Error ? error : new Error(String(error));

  // errorTracker forwards to Sentry itself — reporting again here would
  // duplicate every uncaught render error.
  errorTracker.captureReactError(normalizedError, {
    componentStack: errorInfo.componentStack ?? '',
  });
}

const pathname = window.location.pathname;

if (!pathname.startsWith(BASE_PATH)) {
  // Tenant subdomain root (not under /idp) — initiate SAML directly, skip React.
  // This eliminates the redirect chain: tenant root → /idp → user-portal → SAML.
  void initiateSamlOrFallback();
} else {
  // Normal web-login flow at /idp
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Failed to find the root element');
  }

  createRoot(rootElement, {
    // Last-resort net for render errors that escape every error boundary.
    onUncaughtError: handleUncaughtReactError,
    onRecoverableError: reportReactErrorToSentry,
  }).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}
